import { useEffect, useState, useRef } from 'react';

const PING_URL = '/api/ping';

/**
 * Pings the backend /api/ping endpoint on mount to wake a sleeping
 * Railway server. Retries once on failure. Returns { ready, warming }
 * so the UI can optionally show a warming indicator.
 *
 * Call this ONCE in AppRoutes — it covers all pages.
 */
export default function useServerWake() {
  const [ready, setReady] = useState(false);
  const [warming, setWarming] = useState(true);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    let cancelled = false;

    async function wake() {
      const maxAttempts = 2;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);

          const res = await fetch(PING_URL, {
            method: 'GET',
            signal: controller.signal,
          });
          clearTimeout(timeout);

          if (!cancelled && res.ok) {
            setReady(true);
            setWarming(false);
            console.info(`[HAVN] Server warm (attempt ${attempt})`);
            return;
          }
        } catch (err) {
          console.warn(`[HAVN] Wake attempt ${attempt} failed:`, err.message);
          if (attempt < maxAttempts) {
            await new Promise(r => setTimeout(r, 2000));
          }
        }
      }

      if (!cancelled) {
        setReady(false);
        setWarming(false);
        console.warn('[HAVN] Server wake failed after retries');
      }
    }

    wake();
    return () => { cancelled = true; };
  }, []);

  return { ready, warming };
}
